import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL, USER_URL } from "../../constant/api";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FaRegStar, FaStar, FaStarHalfAlt } from "react-icons/fa";
import MyOrderProductCard from "../../components/Orders/MyOrderProductCard";
import SearchNav from "../../components/Home/SearchNav/SearchNav";
import CustomerImageGrid from "../../components/CustomerImageGrid/CustomerImageGrid";
import CustomerReview from "../../components/CustomerReview/CustomerReview";
import { IoHeartOutline, IoHeartSharp, IoCloseSharp } from "react-icons/io5";
import ProductCard from "../../components/Home/ProductCard/ProductCard";
import ScaleLoader from "react-spinners/ScaleLoader";

function ProductDetails() {
  const { productId } = useParams();
  const [product, setProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(); // Default quantity
  const [wishlist, setWishlist] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [sortedReviews, setSortedReviews] = useState([]);
  const [sortOption, setSortOption] = useState("recent");
  const navigate = useNavigate();
  const [fastMovingProducts, setFastMovingProducts] = useState([]);
  const [visibleReviews, setVisibleReviews] = useState(3); // Initialize with 3 reviews
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  // State to manage whether the full description is shown
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Function to toggle the description visibility
  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  // Open and close popup
  const openPopup = (orderId) => {
    setSelectedOrderId(orderId);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedOrderId(null);
  };

  // Fetch Trending and Fast-Moving Products
  useEffect(() => {
    const fetchData = async (url, setter) => {
      try {
        const response = await axios.get(url);
        setter(response.data.Data || []); // Adjust based on API response structure
        console.log("data",response.data.Data);
        
      } catch (error) {
        console.error(`Error fetching data from ${url}:`, error);
      }
    };

    fetchData(`${BASE_URL}/products/`, setFastMovingProducts);
  }, []);

  // Fetch Product Details
  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/products/${productId}/`);
        const productData = response.data.Data[0] || {};
        setProduct(productData);

        // Set default quantity to min_order_quantity or fallback
        if (productData) {
          setQuantity(productData.min_order_quantity || 1); // Default to min_order_quantity if available, else fallback to 1
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProductDetails();
  }, [productId]);

  // Fetch Related Products
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/products/similar-products/${productId}/`,
        );
        setRelatedProducts(response.data.data || []);
      } catch (err) {
        console.error(err.message);
      }
    };
    fetchRelatedProducts();
  }, [productId]);

  // Fetch Reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/products/reviews/${productId}/`,
        );
        setReviews(response.data.Data || []);
        setSortedReviews(response.data.Data || []);
      } catch (err) {
        console.error(err.message);
      }
    };
    fetchReviews();
  }, [productId]);

  // Handle Sorting
  const handleSort = (criteria) => {
    setSortOption(criteria);
    let sorted = [...reviews];
    if (criteria === "recent") {
      sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (criteria === "highest") {
      sorted.sort((a, b) => b.rating - a.rating);
    } else if (criteria === "lowest") {
      sorted.sort((a, b) => a.rating - b.rating);
    }
    setSortedReviews(sorted);
  };

  // Add to Cart
  const addToCart = async () => {
    if (!product || !product.id) {
      console.error("Invalid product or missing product ID");
      return;
    }

    try {
      // const token = localStorage.getItem('token');
      // const sessionId = localStorage.getItem('sessionid');
      // const headers = token
      //   ? { Authorization: `JWT ${token}` }
      //   : { 'Session-Id': sessionId };

      await USER_URL.post(
        `/orders/cart/`,
        { product_id: product.id, quantity: quantity },
        // { headers }
      );

      navigate("/cart"); // Navigate to cart after adding
    } catch (error) {
      console.error("Error adding product to cart:", error);
    }
  };

  useEffect(() => {
    const getWishlist = async () => {
      // const token = localStorage.getItem("token");
      // const sessionId = localStorage.getItem("sessionid");
      // const headers = token
      //   ? { Authorization: `JWT ${token}` }
      //   : { "Session-Id": sessionId };

      try {
        const response = await USER_URL.get(`/products/wishlist/`);
        setWishlist(response.data.items.map((item) => item.product_id));
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };

    getWishlist();
  }, []); // This ensures the wishlist is fetched only once when the component mounts.

  // Toggle Wishlist
  const toggleWishlist = async (product_id) => {
    try {
      const isWishlisted = wishlist.includes(product_id);
      // const token = localStorage.getItem("token");
      // const sessionId = localStorage.getItem("sessionid");
      // const headers = token
      //   ? { Authorization: `JWT ${token}` }
      //   : { "Session-Id": sessionId };
      // console.log(headers);

      const res = await USER_URL.post(
        `/products/wishlist/`,
        { product_id },
        // { headers }
      );

      // console.log(res);

      setWishlist((prev) =>
        isWishlisted
          ? prev.filter((id) => id !== product_id)
          : [...prev, product_id],
      );
    } catch (error) {
      console.error("Error toggling wishlist:", error);
    }
  };

  const handleDeleteWishlistItem = async (productId) => {
    // const token = localStorage.getItem("token");
    // let sessionId = localStorage.getItem("sessionid");

    // const headers = token
    //   ? { Authorization: `JWT ${token}` }
    //   : { "Session-Id": sessionId };

    // console.log(headers);

    try {
      const response = await USER_URL.delete(
        `/products/wishlist/${productId}/`,
        {
          // data: { product_id: productId },
          // headers,
        },
      );

      if (response.data.Status === "1") {
        // Assuming `wishlist` is a state variable
        setWishlist((prev) => prev.filter((id) => id !== productId));
        console.log("Item deleted successfully:", response.data);
      } else {
        console.error("Unexpected response:", response.data);
      }
    } catch (error) {
      console.error(`Error deleting item ${productId}:`, error);
    }
  };

  // Render Stars Function
  const renderStars = (rating) => {
    const filledStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - filledStars - (hasHalfStar ? 1 : 0);

    return (
      <>
        {[...Array(filledStars)].map((_, index) => (
          <FaStar key={`filled-${index}`} className="h-5 w-5 text-yellow-500" />
        ))}
        {hasHalfStar && <FaStarHalfAlt className="h-5 w-5 text-yellow-500" />}
        {[...Array(emptyStars)].map((_, index) => (
          <FaRegStar key={`empty-${index}`} className="h-5 w-5 text-gray-400" />
        ))}
      </>
    );
  };

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center">
        <ScaleLoader color="#FFAE00" height={40} />
      </div>
    );
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="container mx-auto border-t-2">
      {/* <SearchNav /> */}

      {/* Product Image and Thumbnails and Product Details */}
      <div className="mr-10 flex flex-col gap-8 md:mt-10 md:flex-row md:gap-16">
        {/* Product Image and Thumbnails */}
        <div className="flex-1">
          <CustomerImageGrid
            productImages={product.product_images}
            brandName={product.brand_name}
            brandLogo={product.brand_logo}
          />
        </div>

        {/* Product Details */}
        <div className="ml-5 mt-14 flex-1 items-center text-center md:mt-0 md:items-start md:text-left">
          <h1 className="mb-2 text-lg font-semibold md:text-2xl">
            {product.name}
          </h1>
          <div className="mt-1 flex items-center justify-center space-x-1 sm:mt-2 md:justify-start">
            {renderStars(product?.product_rating || 0)}
            <p className="ml-2 text-sm text-gray-500">
              {product?.product_rating || 0} (
              {product?.product_review_count || 0} Reviews)
            </p>
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-center gap-4 md:justify-start">
            <p className="text-sm font-bold text-red-500">
              -{product.offer_percent}%
            </p>
            <p className="text-sm font-bold text-black md:text-lg">
              {" "}
              {product.currency_symbol}{product.price?.toFixed(2) || "0.00"}
            </p>
            <p className="md:text-md text-sm text-gray-500 line-through">
              {product.currency_symbol}{(product.actual_price || 0).toFixed(2)}
            </p>
          </div>

          {/* Product Description */}
          <div className="mt-4">
            <h2 className="text-lg font-semibold md:text-xl">
              Product Description
            </h2>
            <div
              className={`mt-1 text-sm text-gray-700 md:text-base ${
                !showFullDescription ? "line-clamp-3" : ""
              }`}
            >
              {product.description || "No description available."}
            </div>
            {product.description &&
              product.description.split(" ").length > 20 && ( // Show "Read More" only if description is long
                <div className="flex justify-end">
                  <button
                    onClick={toggleDescription}
                    className="mt-2 text-sm text-blue-500 hover:text-blue-700 focus:outline-none"
                  >
                    {showFullDescription ? "Read Less ▲" : "Read More ▼"}
                  </button>
                </div>
              )}
          </div>

          {/* Dropdown for Quantity Selection */}
          <div className="mt-4 flex flex-wrap items-center justify-center md:justify-start">
            <div className="relative inline-block">
              <label className="my-4 block">
                <span className="text-gray-700">Select Minimum Quantity</span>
                <div className="mt-2 flex flex-row space-x-2 lg:space-x-7">
                  {[
                    product.min_order_quantity,
                    product.min_order_quantity_two,
                    product.min_order_quantity_three,
                    product.min_order_quantity_four,
                    product.min_order_quantity_five,
                  ].map((qty, index) => (
                    <div
                      key={index}
                      onClick={() => setQuantity(qty)}
                      className={`cursor-pointer rounded-lg border p-1 text-center hover:border-brand-primary hover:text-brand-primary lg:p-4 ${
                        quantity === qty
                          ? "bg-brand-primary text-white transition duration-500 ease-in-out hover:text-white"
                          : "bg-white"
                      }`}
                    >
                      {qty}
                    </div>
                  ))}
                </div>
              </label>
            </div>
          </div>

          <p className="text-xs text-gray-600 md:text-sm">
            Delivery in {product.delivery_time || "2-3"} days{" "}
            <span className="ml-5 font-bold">
              • Free Delivery • COD • Secure Transaction
            </span>
          </p>

          <div className="mt-5 flex flex-col justify-center gap-4 md:flex-row md:justify-start md:gap-10">
            <span
              className="w-full"
              onClick={
                () =>
                  wishlist.includes(product.id)
                    ? handleDeleteWishlistItem(product.id) // Call delete function if the product is in the wishlist
                    : toggleWishlist(product.id) // Call toggle function to add the product to the wishlist
              }
            >
              {wishlist.includes(product.id) ? (
                <button className="w-full cursor-pointer rounded border-2 border-red-500 bg-white px-4 py-2 text-red-500 transition hover:bg-red-500 hover:text-white">
                  Remove from Wishlist
                </button>
              ) : (
                <button className="w-full cursor-pointer rounded border-2 border-green-600 px-4 py-2 text-green-600 transition hover:bg-green-600 hover:text-white">
                  Add to Wishlist
                </button>
              )}
            </span>

            <button
              onClick={addToCart}
              className="w-full cursor-pointer rounded border-2 border-green-600 px-4 py-2 text-green-600 transition hover:bg-green-600 hover:text-white"
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>

      {/* Reviews and Trending Products Section */}
      <div className="flex flex-col gap-6 p-5 lg:mt-16 lg:flex-row lg:justify-between lg:p-10">
        <div className="flex w-full flex-col gap-6 lg:flex-row">
          {/* Individual Reviews Section */}
          <div className="flex w-full flex-col lg:w-3/5">
            <h1 className="mb-4 text-center text-lg font-bold lg:text-left lg:text-xl">
              Reviews
            </h1>
            <div className="flex flex-col gap-3 rounded-md bg-[#FFDED0] p-5 md:flex-row md:items-center md:justify-between">
              <h2 className="text-center font-semibold md:text-left">
                {product?.product_rating || "0.0"} Rating{" "}
                <span className="font-normal text-gray-400">
                  ({reviews.length} Reviews)
                </span>
              </h2>
              <button
                className="mt-3 rounded-md border border-green-500 bg-white px-4 py-2 text-green-500 transition-colors duration-200 hover:bg-green-500 hover:text-white md:mt-0"
                onClick={() => openPopup(product.id)}
              >
                Write Review
              </button>
            </div>

            {/* Popup for Customer Review */}
            <CustomerReview
              isOpen={isPopupOpen}
              onClose={closePopup}
              orderId={selectedOrderId}
            />

            {/* Sort Options (Only show if there are reviews) */}
            {sortedReviews.length > 0 && (
              <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center">
                <p className="text-base font-semibold text-gray-700">Sort by</p>
                <div className="flex flex-wrap gap-2">
                  {["recent", "highest", "lowest"].map((option) => (
                    <button
                      key={option}
                      onClick={() => handleSort(option)}
                      className={`cursor-pointer rounded-md border border-gray-500 px-4 py-2 font-medium transition-colors duration-200 ${
                        sortOption === option
                          ? "bg-[#FFDED0] text-black"
                          : "bg-white text-gray-500 hover:bg-[#FFDED0] hover:text-black"
                      }`}
                    >
                      {option === "recent"
                        ? "Most Recent"
                        : option === "highest"
                          ? "Highest Rating"
                          : "Lowest Rating"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Individual Reviews */}
            <div className="mt-4 h-[420px] overflow-y-auto">
              {sortedReviews.length > 0 ? (
                sortedReviews.slice(0, visibleReviews).map((review, index) => (
                  <div
                    key={index}
                    className="m-2 flex flex-col gap-2 rounded-md bg-slate-100 p-5"
                  >
                    <h2 className="font-semibold">
                      {review.username || "Anonymous"}
                    </h2>
                    <div className="flex items-center space-x-1">
                      {renderStars(review?.rating || 0)}
                      <p className="ml-2 text-sm text-gray-500">
                        {review.rating || "0.0"}
                      </p>
                    </div>
                    <p className="font-light text-gray-700">
                      {review.comments || "No comment provided."}
                    </p>
                  </div>
                ))
              ) : (
                <p className="mt-10 text-center text-gray-500">
                  No reviews available for this product.
                </p>
              )}
            </div>
            {/* {sortedReviews.length > visibleReviews && (
          <div className=" flex justify-end mr-3">
            <Link
              to={`/product-details/reviews/${product.id}`}
              className="text-green-500 hover:text-blue-500"
              onClick={() => setVisibleReviews(sortedReviews.length)}
            >
              View More
            </Link>
          </div>
        )} */}

            {/* Render View More Link Conditionally */}
            {sortedReviews.length > visibleReviews &&
              sortedReviews.length > 0 && (
                <div className="mr-3 flex justify-end">
                  <Link
                    to={`/product-details/reviews/${product.id}`}
                    className="text-green-500 hover:text-blue-500"
                  >
                    View More
                  </Link>
                </div>
              )}
          </div>

          {/* Trending Products Section */}
          <div className="w-full lg:w-2/5">
            <MyOrderProductCard
              className
              products={fastMovingProducts}
              title="Fast Moving Items"
              layout="vertical-2-horizontal-2"
            />
          </div>
        </div>
      </div>
      <div className="p-5">
        <ProductCard products={relatedProducts} title="Related Products" />
      </div>
    </div>
  );
}

export default ProductDetails;
